import classNames from "classnames";
import { PreviewRenderer, slateToPlainText } from "./PreviewRenderer";
import { timeDifference } from "../../lib/utils/time";
import {useEffect, useState, useRef, useCallback} from "react";
import { Icon } from "../common/Icon";
import {Chat, Message as MessageType} from "../../lib/types";
import { useUserContext } from "../../providers/UserContext";
import { MessageComposer } from "./MessageComposer";
import {Descendant, Editor, Transforms} from "slate";
import { editMessage, react, removeReaction } from "../../lib/database";
import Picker from "@emoji-mart/react";
import data, { EmojiMartData } from "@emoji-mart/data";
import { User } from "firebase/auth";
import { truncate } from "../../lib/utils/formatting";
import { Button } from "../common/Button";
import { Link } from "react-router-dom";
import rangy from "rangy";
import "rangy/lib/rangy-classapplier";
import "rangy/lib/rangy-serializer";
import {useLayoutContext} from "../../providers/LayoutProvider";
import {Quote} from "../common/Quote";
import Dropdown from "../common/Dropdown";
import {useModalContext} from "../../providers/ModalProvider";
import {useSidebarIcons} from "../../lib/hooks/useSidebarIcons";
import {ReactEditor} from "slate-react";

export const Message = ({
                          author,
                          content,
                          timestamp,
                          isRead = false,
                           isOriginal = false,
                          message,
                          previousMessage,
                          lastMessage,
                          scrollChatToBottom,
                          onReply,
                          scrollToMessage,
                          highlight,
                          onThread,
                          threadText,
                          chat,
                          showAuthor=true,
                          showTimestamp=true,
                          chatEditor
                        }: {
  author: string;
  content: string;
  isRead?: boolean;
  isOriginal?: boolean;
  timestamp: number;
  message: MessageType;
  previousMessage?: MessageType;
  lastMessage: boolean;
  scrollChatToBottom: () => void;
  onReply?: (message: MessageType, text?: string) => void;
  onThread?: (message: MessageType, markedHtml: string, selectedHtml: string) => void;
  scrollToMessage?: (message: MessageType) => void;
  highlight?: boolean;
  threadText?: string;
  showAuthor?: boolean;
  showTimestamp?: boolean;
  chat: Chat;
  chatEditor?: ReactEditor;
}) => {
  const emojis = data as EmojiMartData;
  let className = "flex group";
  let textClass = "font-style-body-md";
  let authorClass = "font-style-label-md";
  const {isMobileResolution} = useLayoutContext();
  if (isRead) {
    authorClass += " !text-gray-4 ";
    if (!isOriginal) {
      textClass += " !text-gray-4 read-message";
    } else {
      textClass += " !text-gray-4 pl-2 border-l-[3px] border-l-gray-6";
    }
  } else {
    textClass += " !text-gray-1";
  }
  const json = content ? JSON.parse(content || "") : "";
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 60000);
    return () => clearInterval(interval);
  }, []);
  const { isOpen, openModal, closeModal } = useModalContext();
  const { user, getMember, sidebar } = useUserContext();
  const isMine = message?.authorId === user?.uid;
  const [isEditting, setIsEditting] = useState(false);
  const [editor, setEditor] = useState<ReactEditor | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const [hoverContent, setHoverContent] = useState("");
  const handleEmojiSelect = (emoji: any) => {
    if (message && user) {
      react(message, user, emoji.id);
    }
    setShowEmojiPicker(false); // Hide the picker after selection
  };

  if (showAuthor) {
    className += "mt-4";
  }
  const deleteMessage = useCallback(() => {
    openModal(<div>
      <div className="font-style-label-lg">Delete message</div>
      <div className="font-style-body-md text-gray-2 mt-2">You are about to <span className="font-bold">delete</span> the following message. This action is irreversible.</div>
      <div className="preview flex rounded-md border border-gray-6 p-3 mt-3 gap-3">
        <div className="quote-bar rounded-full bg-gray-8 min-h-4 w-1">

        </div>
        <div className="flex flex-col gap-1">
          <div className="author flex items-center gap-1 font-style-label-md">
            <img src={authorData?.avatar} alt="" className={"w-4 h-4 rounded-md"} onError={(e) => {e.currentTarget.onerror=null; e.currentTarget.src='/avatar.png'}}/>
            {authorData?.displayName}
          </div>
          <div className={"max-h-[30vh] overflow-auto"}>
            <PreviewRenderer content={JSON.parse(message.content)}/>
          </div>
          <div className={"font-style-body-sm text-gray-3 mt-1 inline-flex items-center gap-1"}>
            From a chat in <span className={"inline-flex items-center gap-2"}>
                            {
                              names.map((name, idx) => (
                                <span key={"recipient-"+idx} className={"inline-flex gap-1"}>
                                <span className="w-4">{icons[idx]}</span>
                                  {name}{idx < names.length - 1 ? ", " : ""}
                              </span>
                              ))
                            }
                          </span> •
            <span className=""
                  title={new Date(timestamp).toLocaleString()}
            >
                                {" "}{timeDifference(now, timestamp)}
                             </span>
          </div>
        </div>
      </div>
      <div className="flex gap-2 mt-4 justify-between">
        <Button variant={"tertiary"} className={"!border-gray-6"} onClick={() => {
          closeModal();
        }}>Cancel</Button>
        <Button variant={"primary-danger"} onClick={async (e) => {
          await editMessage({...message, content: "[]", plainText: "", deleted: true});
          e.stopPropagation();
          e.preventDefault();
          closeModal();
        }} autoFocus>Delete</Button>
      </div>
    </div>)
  },[message]);
  // Edit on keydown (up) if last message
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {

      if (lastMessage && isMine && event.key === "ArrowUp" && !isEditting) {
        const isChatEditorEmpty = !chatEditor || slateToPlainText(chatEditor.children).trim().length === 0;
        if(isChatEditorEmpty) {
          event.preventDefault();
          setIsEditting(true);
          setTimeout(() => {
            scrollChatToBottom();
          }, 0);
        }
      }
      // Cancel editing on Esc
      if (isEditting && event.key === "Escape") {
        setIsEditting(false);
      }
      const isEditEditorEmpty = !editor || slateToPlainText(editor.children).trim().length === 0;
      if(event.key === "Enter" && isEditting && isMine && isEditEditorEmpty && !isOpen) {
        event.stopPropagation();
        event.preventDefault();
        deleteMessage();
      }
    };
    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsEditting(false);
        setShowEmojiPicker(false);
        setShowActionsDropdown(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [lastMessage, editor, isOpen, isEditting, chatEditor]);

  const reactions = message.reactions || {};
  const didIReact = Object.keys(reactions).some((reaction) =>
    Object.keys(reactions[reaction]).includes(user?.uid || "")
  );
  if (highlight) {
    className += " !bg-blue-4";
  }
  const [activeRangeMessage, setActiveRangeMessage] =
    useState<MessageType | null>(null);
  const [selection, setSelection] = useState<any>(null);
  const isSelectionWithinSingleMessage = (id: string) => {
    const selection = window.getSelection();
    if (selection && !selection.isCollapsed) {
      const startContainer = selection.anchorNode?.parentElement;
      const endContainer = selection.focusNode?.parentElement;

      // Ensure start and end containers belong to the same message
      const startMessage = startContainer?.closest("[data-message-id]");
      const endMessage = endContainer?.closest("[data-message-id]");
      return (
        startMessage &&
        endMessage &&
        startMessage === endMessage &&
        startMessage.getAttribute("data-message-id") === id
      );
    }
    return false;
  };
  const [selectionBounds, setSelectionBounds] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [showCreateThread, setShowCreateThread] = useState(false);

  const messageContentRef = useRef<HTMLDivElement>(null);

  // Helper function to find the corresponding node in the clone
  const findNodeInClone = (
    originalNode: Node,
    originalRoot: Node,
    clonedRoot: Node
  ): Node => {
    const path = [];
    let node: Node | null = originalNode;
    while (node && node !== originalRoot) {
      const parent = node.parentNode as HTMLElement;
      if (!parent) break;
      const index = Array.prototype.indexOf.call(parent.childNodes, node);
      path.unshift(index);
      node = parent;
    }
    let clonedNode: Node = clonedRoot;
    for (const index of path) {
      clonedNode = clonedNode.childNodes[index];
    }
    return clonedNode;
  };

  useEffect(() => {
    const handleSelectionChange = () => {
      setShowCreateThread(false);
    }
    document.addEventListener("selectionchange", () => {
      handleSelectionChange();
    });
    return () => {
      document.removeEventListener("selectionchange", () => {
        handleSelectionChange();
      });
    };
  }, []);

  useEffect(() => {
    if(message.deleted) {
      setShowCreateThread(false);
      setIsEditting(false);
      closeModal();
    }
  }, [message.deleted]);
  useEffect(() => {
    if(!isEditting && chatEditor) {
      ReactEditor.focus(chatEditor)
      const endPoint = Editor.end(chatEditor, []);
      Transforms.select(chatEditor, endPoint);
    }
  }, [isEditting]);
  const createThreadFromSelection = () => {
    const selectionObj = rangy.getSelection();
    const selectedText = selectionObj.toString();
    if (selectionObj && selectionObj.rangeCount > 0) {
      const messageContentElement = messageContentRef.current;
      if (!messageContentElement) return;

      const range = selectionObj.getRangeAt(0);

      // Clone the message content element
      const clonedMessageContent = messageContentElement.cloneNode(true) as HTMLElement;

      // Adjust the range to point to the nodes within the cloned content
      const clonedRange = rangy.createRange();
      clonedRange.setStart(
        findNodeInClone(range.startContainer, messageContentElement, clonedMessageContent),
        range.startOffset
      );
      clonedRange.setEnd(
        findNodeInClone(range.endContainer, messageContentElement, clonedMessageContent),
        range.endOffset
      );

      // Create a class applier for highlighting
      const classApplier = rangy.createClassApplier("text-highlight", {
        elementTagName: "span",
        elementAttributes: {},
        normalize: true,
      });

      // Apply the class applier to the cloned range
      classApplier.applyToRange(clonedRange);

      // Get the innerHTML of the cloned message content with the highlighted selection
      const selectedHtml = clonedMessageContent.innerHTML;

      // Pass the HTML to the onThread function
      onThread && onThread(message, selectedHtml, selectedText);

      setShowCreateThread(false);

      // Clear the selection
      selectionObj.removeAllRanges();
    }
  };

  const replyFromSelection = () => {
    const selectionObj = rangy.getSelection();
    let selectedText = selectionObj.toString();
    if (selectionObj && selectionObj.rangeCount > 0) {
      if(onReply) {
        const messageText = message.plainText;
        if(messageText.search(selectedText) > 0) {
          selectedText = "..." + selectedText;
        }
        if (messageText.search(selectedText) + selectedText.length < messageText.length) {
          selectedText = selectedText + "...";
        }
        onReply(message, selectedText);
      }
    }
  }

  const authorData= getMember(message.authorId);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const sidebarItem = sidebar.find(item => item.id === chat.sidebarItemId);
  const [names,icons] = useSidebarIcons(sidebarItem);
  return (
    <div data-message-id={message.id} className={classNames(
      "-mx-4",
      showAuthor ? "" : "-mt-2"
    )} onMouseLeave={() => {
      setShowActionsDropdown(false);
    }}>
      {selectionBounds && showCreateThread && (
        <div
          className="fixed z-50 bg-gray-900 text-white p-2 rounded flex"
          style={{
            top: selectionBounds.top - 55,
            left: selectionBounds.left - 80,
          }}
        >
          <Button onClick={createThreadFromSelection} className={"rounded-none !rounded-l-lg"}>Start Thread</Button>
          <Button onClick={replyFromSelection} className={"rounded-none !rounded-r-lg"}>Reply</Button>
        </div>
      )}
      <div className={className}>
        {showEmojiPicker && (
          <div>
            <div
              className="backdrop fixed z-40 left-0 top-0 w-full h-full"
              onClick={() => {
                setShowEmojiPicker(false);
              }}
              style={isMobileResolution ? {
                background: "rgba(0,0,0,.3)"
              } : {}}
            ></div>
            <div
              className="fixed z-50"
              style={!isMobileResolution ? {
                top: Math.min(pickerPosition.top - 50, window.innerHeight - 500),
                left: pickerPosition.left - 380,
              } : {
                top: "50%",
                left: "50%",
                translate: "-50% -50%",
              }}
            >
              <Picker data={data} onEmojiSelect={handleEmojiSelect} autoFocus={true} theme={"light"} />
            </div>
          </div>
        )}
        <div className="flex flex-col gap-1 w-full relative group block hover:bg-gray-9 pr-4 pl-20 py-2 relative">
          {showAuthor && (
            <div className="flex justify-between w-full group">
              <div className={authorClass}>
                <div className="avatar absolute left-[32px]">
                  {message.deleted
                    ? (<div className="rounded-md w-10 h-10 bg-gray-7 flex items-center justify-center">
                      <Icon name={"speaker_notes_off"} size={"medium"}/>
                    </div>)
                    : (authorData?.avatar ? (
                    <div
                      className={classNames(
                        "rounded-md w-10 h-10",
                        isRead && "opacity-60"
                      )}
                      style={{
                        backgroundImage: `url(${authorData.avatar})`,
                        backgroundSize: "cover",
                        opacity: isRead ? 0.6 : 1,
                      }}
                    ></div>
                  ) : (
                    <div className="rounded-md w-10 h-10 bg-gray-7"></div>
                  ))}
                </div>

                {message.deleted ? "Deleted" : author}
                <span className={
                  classNames(
                    "font-style-body-xs text-gray-3 ml-1 transition-all",
                    !showTimestamp && "opacity-0 group-hover:opacity-100"
                  )
                }
                      title={new Date(timestamp).toLocaleString()}
                >
                  {" "}
                  · {timeDifference(now, timestamp)}
                </span>
              </div>
            </div>
          )}
          <div
            className={
              classNames(
                "actions z-10 px-2 py-1 right-4 shadow-elevation-2 bg-white flex gap-2 items-center absolute pointer-events-none group-hover:pointer-events-auto opacity-0 group-hover:opacity-100 hover:!opacity-100 transition-all rounded-md border border-gray-7",
                showAuthor ? "-top-3" : "-top-6",
                (message.deleted || isEditting) && "!hidden",
                showEmojiPicker && "!opacity-100 !pointer-events-auto"
              )}
          >
            {!isOriginal && (<button
              className={"hover:bg-gray-8 h-5 w-5 flex justify-center items-center rounded-md"}
              onClick={(e) => {
                // Positionate to the right of the button
                setPickerPosition({top: e.clientY, left: e.clientX});
                setShowEmojiPicker(true);
              }}
            >
              <Icon name={"add_reaction"} size={"xsmall"}/>
            </button>)}
            {onReply && (<button
              className={"hover:bg-gray-8 h-5 w-5 flex justify-center items-center rounded-md"}
              onClick={(e) => {
                onReply(message);
              }}
            >
              <Icon name={"reply"} size={"xsmall"}/>
            </button>)}
            {onThread && (<button
              className={"hover:bg-gray-8 h-5 w-5 flex justify-center items-center rounded-md"}
              onClick={(e) => {
                if(messageContentRef.current) {
                  const html = messageContentRef.current.innerHTML;
                  const text = messageContentRef.current.innerText;
                  onThread(message, html, text);
                }
              }}
            >
              <Icon name={"chat_add_on"} size={"xsmall"}/>
            </button>)
            }
            {isMine && !message.deleted && !isOriginal && (
              <button
                className={"hover:bg-gray-8 h-5 w-5 flex justify-center items-center rounded-md"}
                onClick={() => {
                  setIsEditting(true);
                }}
              >
                <Icon name={"edit"} size={"xsmall"}/>
              </button>
            )}
            {isMine && (
              <button
                className={"hover:bg-gray-8 h-5 w-5 flex justify-center items-center rounded-md"}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowActionsDropdown(true);
                }}
              >
                  <Icon name={"more_vert"}
                        size="xsmall"/>
              </button>
            )}

              <div className={"absolute right-0 w-full top-[85%]"}>
                <Dropdown
                  onClose={() => {
                    setShowActionsDropdown(false);
                  }}
                  open={showActionsDropdown}
                  className={showActionsDropdown ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}
                >
                  {isMine && (
                    <Button variant={"tertiary"} className={"w-full !justify-start pointer !text-danger-1 hover:!bg-danger-4"} onClick={deleteMessage}>
                      Delete message
                    </Button>
                    )}
                </Dropdown>
              </div>
          </div>
          {message.parent && (
            <button
              className={"relative rounded-t-lg py-1"}
              onClick={() => {
                if (scrollToMessage) {
                  scrollToMessage(message.parent as MessageType);
                }
              }}
            >
              <Quote isRead={isRead}>
                <div className="flex flex-col items-start w-full">
                  <div className="font-style-label-md flex-shrink-0">
                    <span className={"line-clamp-1"}>{message.parent.authorName}:</span>
                  </div>
                  <div className="font-style-body-md flex-grow text-left">
                    <span className={"line-clamp-1"}>
                      {message.replySelection || message.parent.plainText}
                    </span>
                  </div>
                </div>
              </Quote>
            </button>
          )}
          <div className={textClass}>
            {isEditting ? (
              <div>
                <MessageComposer
                  initialValue={JSON.parse(message.content) as Descendant[]}
                  onSend={(content) => {
                    const newMessage = {
                      ...message,
                      content: JSON.stringify(content.children),
                      plainText: slateToPlainText(content.children),
                    };
                    editMessage(newMessage);
                    setIsEditting(false);
                  }}
                  onReady={(editor) => {
                      setEditor(editor);
                      const endPoint = Editor.end(editor, []);
                      Transforms.select(editor, endPoint);
                    }
                  }
                  hasCancelButton={true}
                  onCancel={() => {
                    setIsEditting(false);
                  }}
                  submitLabel={"Save"}
                />
              </div>
            ) : (
              <div>
                {
                  message.deleted &&
                  (<div className="text-gray-4">This message was deleted</div>)
                }
                {!message.system && json && Array.isArray(json) && (
                  <div
                    className={"to-fade"}
                    ref={messageContentRef}
                    onMouseUp={(event) => {
                      setActiveRangeMessage(message);
                      if (window.getSelection()) {
                        const newSelection = window.getSelection();
                        if (newSelection && newSelection.rangeCount > 0) {
                          if (isSelectionWithinSingleMessage(message.id)) {
                            setSelection(newSelection.getRangeAt(0));
                            setSelectionBounds({
                              top: event.clientY,
                              left: event.clientX,
                            });
                            setShowCreateThread(true);
                          } else {
                            setSelectionBounds(null);
                            setShowCreateThread(false);
                          }
                        } else {
                          setSelectionBounds(null);
                          setShowCreateThread(false);
                        }
                      }
                    }}
                  >
                    {hoverContent ? (
                      <div
                        dangerouslySetInnerHTML={{ __html: hoverContent }}
                      ></div>
                    ) : threadText ? (
                      <div
                        dangerouslySetInnerHTML={{ __html: threadText }}
                      ></div>
                    ) : (
                      json ? <PreviewRenderer content={json} /> : ""
                    )}
                  </div>
                )}
                <div className="reactions relative flex gap-2 z-[99] mt-1">
                  {message.reactions &&
                    Object.keys(message.reactions).map((reaction) => (
                      <button
                        key={reaction}
                        onClick={() => {
                          if (didIReact) {
                            removeReaction(message, user as User, reaction);
                          } else {
                            react(message, user as User, reaction);
                          }
                        }}
                        onMouseEnter={(event) => {
                          const button = event.currentTarget;
                          const popover = button.querySelector(".reaction-popover") as HTMLDivElement;
                          if (popover) {
                            const buttonRect = button.getBoundingClientRect();
                            const viewportHeight = window.innerHeight;
                            const spaceBelow = viewportHeight - buttonRect.bottom;
                            // Adjust position based on available space
                            if (spaceBelow < 200) {
                              popover.classList.add("show-above");
                              popover.classList.remove("show-below");
                            } else {
                              popover.classList.add("show-below");
                              popover.classList.remove("show-above");
                            }
                          }
                        }}
                        className={classNames(
                          "reaction py-1 group/emoji flex justify-center font-style-body-sm items-center border transition-all rounded-lg h-[25px] w-[40px] px-2",
                          didIReact ? "bg-[#F9F7F2] border-[#D4CFC3]" : "bg-gray-11 border-transparent hover:bg-[#F9F7F2] hover:border-[#D4CFC3]",
                        )}
                      >
                        <div className={isRead ? "grayscale flex" : "flex   "}>
                          {emojis.emojis[reaction].skins[0].native}
                          <span className="ml-1">
                            {message?.reactions &&
                              Object.keys(message.reactions[reaction]).length}
                          </span>
                        </div>

                        <div className="reaction-popover bg-gray-1 py-1 px-3 hidden group-hover/emoji:flex flex-col items-start absolute justify-center text-left left-0 z-[99] rounded-[4px] ">
                          <div className="triangle bg-black rotate-45 h-[6px] w-[6px] absolute"></div>
                          <div className="!text-white font-style-label-sm">
                            {message?.reactions &&
                              Object.keys(message.reactions[reaction]).map(
                                (userId, idx) => {
                                  const lastItem = Object.keys(message?.reactions?.[reaction] || {}).length - 1 === idx
                                  return (
                                    <span key={userId}>
                                  {idx > 0 && (lastItem ? " and " : ", ")} {getMember(userId)?.displayName}
                                </span>
                                  )}
                              )}
                          </div>
                          <div className={"text-gray-4 font-style-label-sm"}>
                            reacted with {emojis.emojis[reaction].skins[0].native}
                          </div>
                        </div>
                      </button>
                    ))}
                </div>
                {!isOriginal &&
                  message.threads &&
                  message.threads.length > 0 && (
                    <div className="relative threads flex flex-col gap-2 pl-7 pt-3 mt-3">
                      <div className="before absolute left-4 -mt-3 border-l border-gray-5 h-[calc(100%-23px)] w-3"></div>
                      {message.threads.map((thread) => (
                        <Link
                          to={"/chat/" + thread.id}
                          key={thread.id}
                          className="relative thread flex bg-gray-8 p-3 rounded-md hover:bg-yellow-4"
                          onMouseEnter={() => {
                            setHoverContent(thread.threadHtml || "");
                          }}
                          onMouseLeave={() => {
                            setHoverContent("");
                          }}
                        >
                          <div className="before absolute -left-3 border-l rounded-bl-md border-b border-gray-5 h-3 w-3 z-0"></div>
                          <div className="relative font-style-body-xs text-gray-2">
                            {thread.authorName}:
                          </div>
                          <div className="relative font-style-body-xs text-gray-2 ml-1">
                            {truncate(thread.summary, 80)}
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
